import type { JobListing } from "./index";

// Very common words (EN + FR) and generic job-posting filler that carry no
// signal for matching. Kept small on purpose.
const STOPWORDS = new Set([
  "the",
  "and",
  "for",
  "with",
  "you",
  "your",
  "our",
  "are",
  "will",
  "who",
  "this",
  "that",
  "have",
  "has",
  "from",
  "job",
  "role",
  "team",
  "work",
  "working",
  "experience",
  "years",
  "year",
  "strong",
  "ability",
  "skills",
  "skill",
  "including",
  "across",
  "using",
  "new",
  "help",
  "build",
  "building",
  "looking",
  "join",
  "joining",
  "make",
  "world",
  "les",
  "des",
  "une",
  "aux",
  "pour",
  "avec",
  "vous",
  "nous",
  "notre",
  "vos",
  "qui",
  "que",
  "dans",
  "sur",
  "est",
  "sont",
  "poste",
  "emploi",
  "offre",
  "equipe",
  "travail",
  "experience",
  "ans",
  "competences",
  "recherche",
  "recherchons",
  "candidat",
  "mission",
  "missions",
  "profil",
  "poste",
  "chez",
  "dun",
  "dune",
  "cette",
  "votre",
  "plus",
  "tout",
  "tous",
]);

const tokenize = (text: string): string[] =>
  (text.toLowerCase().match(/[\d#+.a-zà-ÿ]{3,}/g) ?? []).filter((token) => !STOPWORDS.has(token));

// The set of meaningful terms in a resume, computed once per resume.
export const resumeTerms = (resumeText: string): Set<string> => new Set(tokenize(resumeText));

// A 0-100 fit score: how well the job's terms are covered by the resume, with
// the job title weighted heavily (the strongest fit signal). Purely a ranking
// heuristic — relative order matters more than the absolute number.
export const fitScore = (terms: Set<string>, job: JobListing): number => {
  if (terms.size === 0) return 0;

  const titleTerms = [...new Set(tokenize(job.title))];
  const bodyTerms = [...new Set(tokenize(`${job.tags.join(" ")} ${job.description}`))];

  const hitRatio = (list: string[]) =>
    list.length === 0 ? 0 : list.filter((term) => terms.has(term)).length / list.length;

  const titleHit = hitRatio(titleTerms);
  const bodyHit = hitRatio(bodyTerms);

  return Math.round(Math.min(100, (titleHit * 0.65 + bodyHit * 0.35) * 130));
};

export type ScoredJob = JobListing & { fit: number };

export const rankJobs = (jobs: JobListing[], resumeText: string): ScoredJob[] => {
  const terms = resumeTerms(resumeText);
  return jobs.map((job) => ({ ...job, fit: fitScore(terms, job) })).sort((a, b) => b.fit - a.fit);
};
